import { SaveWarning, SaveWarningReviewOutcome } from '../types';

export interface SaveWarningAcknowledgement {
  acknowledgedWarningIds: string[];
  acknowledgementComment: string;
  warningReviewOutcomes: Record<string, SaveWarningReviewOutcome>;
}

/** Create the UUID shared by every request in one enforce-mode Remote DC save loop. */
export function createSaveMaterializationAttemptId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`;
}

/** Accumulate acknowledgements across repeated enforce-mode 422 responses.
 *
 * A retry can surface a new warning while previously acknowledged warnings are
 * still live. Dropping the earlier ids makes the client oscillate between the
 * two warning sets, so retain every id and every comment until the save applies.
 */
export function accumulateSaveWarningAcknowledgement(
  currentIds: string[] | undefined,
  currentComment: string | undefined,
  currentOutcomes: Record<string, SaveWarningReviewOutcome> | undefined,
  warnings: SaveWarning[],
  nextComment: string,
  nextOutcome: SaveWarningReviewOutcome
): SaveWarningAcknowledgement {
  const acknowledgedWarningIds = [
    ...new Set([...(currentIds ?? []), ...warnings.map((warning) => warning.warningId)]),
  ];
  const acknowledgementComment = [currentComment?.trim(), nextComment.trim()]
    .filter((comment): comment is string => Boolean(comment))
    .join('\n\n');
  const warningReviewOutcomes = { ...(currentOutcomes ?? {}) };
  for (const warning of warnings) warningReviewOutcomes[warning.warningId] = nextOutcome;
  return { acknowledgedWarningIds, acknowledgementComment, warningReviewOutcomes };
}
